
import { DandDFeatureSource } from "@dndModels/feature_sources/SystemFeatureSource";
import { DataType } from "@dndModels/Types";
import { 
  Feature,
  FeatureBase,
  // Limitation 
} from "@models";

// import DataUtilities from "../utilities/common_utilities";
// import { DataUtilitiesClass } from "../utilities/common_utilities_class";

/**
 * Conditions are any state that can be 
 * applied to a character: 
 * Prone, Exhausted, Blessed, Raging, etc.
 * 
 * Lots of them have associated Features.
 * Prone - Disadvantage on Attacks, Advantage on Incoming Attacks, Half Movement Speed
 * Blessed - +1d4 on Attacks and Saving Throws
 * Raging - +2 to damage rolls, Resistances, etc.
 * 
 * There are also features that can be on 
 * other things which are only active when
 * certain conditions are present.
 * 
 * There are also abilities and spells which
 * automatically apply Conditions (Rage). 
 * (Low Priority)
 */

export class Condition extends DandDFeatureSource {
  data_type: DataType = "condition";
  feature_source_type = "Condition";
  immunity_exists = false;
  common = false;
  max_level = 0;
  class_ids: string[] = [];
  subclass_ids: string[] = [];
  encumbrance = false; // Whether or not this is an encumbrance condition
  // These are numbers to be multiplied by the characters Strength score 
  // and their size multiplier. 
  // Level will be 0 if below the lowest weight threshold, 
  // 1 if between it and the next, etc.
  encumbrance_levels: { 
    threshold: number,
    name: string,
    color: string,
    true_id: string
  }[] = []; 
  exhaustion = false; // Whether or not this is an exhaustion condition
  // features: Feature[];
  reduce_on_long_rest = 1;
  
  constructor(obj?: any) {
    super(obj, "Condition");
    if (obj) {
      this.immunity_exists = obj.immunity_exists ? obj.immunity_exists : false;
      this.common = obj.common !== undefined ? obj.common : (obj.always_show !== undefined ? obj.always_show : false);
      this.max_level = obj.max_level ? obj.max_level : 0;
      this.class_ids = obj.class_ids ? obj.class_ids : [];
      this.subclass_ids = obj.subclass_ids ? obj.subclass_ids : [];
      if (this.feature_bases.length === 0 && obj.features && obj.features.length > 0) {
        // const common_util = DataUtilities.getInstance();
        const features: Feature[] = [];
        obj.features.forEach((o: any) => {
          o.source_id = this._id;
          o.source_type = this.feature_source_type;
          const feature = new Feature(o);
          features.push(feature);
        });
        const feature_base = new FeatureBase();
        feature_base.feature_source_id = this._id;
        feature_base.feature_source_type = this.feature_source_type;
        feature_base.features = features;
        this.feature_bases.push(feature_base);
      }
      this.encumbrance = obj.encumbrance ? obj.encumbrance : false;
      this.encumbrance_levels = obj.encumbrance_levels ? obj.encumbrance_levels : [];
      this.reduce_on_long_rest = obj.reduce_on_long_rest !== undefined ? obj.reduce_on_long_rest : 1;
      this.exhaustion = obj.exhaustion ? obj.exhaustion : this.name === "Exhaustion";
    }
  }

  // load(obj: any): void {
  //   super.load(obj);
  //   if (obj) {
  //     this.immunity_exists = obj.immunity_exists ? obj.immunity_exists : false;
  //     this.common = obj.common !== undefined ? obj.common : (obj.always_show !== undefined ? obj.always_show : false);
  //     this.max_level = obj.max_level ? obj.max_level : 0;
  //     this.class_ids = obj.class_ids ? obj.class_ids : [];
  //     this.subclass_ids = obj.subclass_ids ? obj.subclass_ids : [];
  //     if (this.feature_bases.length === 0 && obj.features && obj.features.length > 0) {
  //       // const common_util = DataUtilities.getInstance();
  //       const features: Feature[] = [];
  //       obj.features.forEach((o: any) => {
  //         o.source_id = this._id;
  //         o.source_type = this.feature_source_type;
  //         const feature = new Feature(o);
  //         features.push(feature);
  //       });
  //       const feature_base = new FeatureBase();
  //       feature_base.feature_source_id = this._id;
  //       feature_base.feature_source_type = this.feature_source_type;
  //       feature_base.features = features;
  //       this.feature_bases.push(feature_base);
  //     }
  //     this.encumbrance = obj.encumbrance ? obj.encumbrance : false;
  //     this.encumbrance_levels = obj.encumbrance_levels ? obj.encumbrance_levels : [];
  //     this.reduce_on_long_rest = obj.reduce_on_long_rest !== undefined ? obj.reduce_on_long_rest : 1;
  //     this.exhaustion = obj.exhaustion ? obj.exhaustion : this.name === "Exhaustion";
  //   }
  // }
  
  get common_string() {
    return this.common ? "Common" : "";
  }

  get_detail_format() {
    if (this.common) {
      const str = `${super.get_detail_format()}; ${this.common_string}`;
      return str.trim();
    } else {
      return super.get_detail_format();
    }
  }

  toDBObj(): any {
    const feature_source_obj = super.toDBObj();
    return {
      ...feature_source_obj,
      max_level: this.max_level,
      immunity_exists: this.immunity_exists,
      common: this.common,
      class_ids: this.class_ids,
      subclass_ids: this.subclass_ids,
      encumbrance: this.encumbrance,
      encumbrance_levels: this.encumbrance_levels,
      reduce_on_long_rest: this.reduce_on_long_rest,
      exhaustion: this.exhaustion
    };
  }

  toLight(): any {
    const feature_source_obj = super.toLight();
    return {
      ...feature_source_obj,
      max_level: this.max_level,
      immunity_exists: this.immunity_exists,
      common: this.common,
      class_ids: this.class_ids,
      subclass_ids: this.subclass_ids,
      encumbrance: this.encumbrance,
      encumbrance_levels: this.encumbrance_levels,
      reduce_on_long_rest: this.reduce_on_long_rest,
      exhaustion: this.exhaustion
    };
  }

  copy(copyMe: Condition, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    this.max_level = copyMe.max_level;
    this.immunity_exists = copyMe.immunity_exists;
    this.common = copyMe.common;
    this.class_ids = copyMe.class_ids;
    this.subclass_ids = copyMe.subclass_ids;
    this.encumbrance = copyMe.encumbrance;
    this.encumbrance_levels = [...copyMe.encumbrance_levels];
    this.reduce_on_long_rest = copyMe.reduce_on_long_rest;
    this.exhaustion = copyMe.exhaustion;
  }

  clone(drop_ids = false): Condition {
    const new_obj = new Condition();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}